import { Address, Country } from 'APIs/fragments';
import {
  CreateMerchantPersonRequest,
  MerchantPerson,
  MerchantPersonRole,
  UpdateMerchantPersonRequest,
} from 'APIs/merchant.persons/merchant.persons.types';
import { merchantPersonRoles } from 'Constants/Constants';

export const defaultIdType = 'DriverLicence';
export const defaultCountryId = 'AU' as Country;

export type PersonFormProps = {
  ownerId: string | null;
  title: string;
  roles: Array<{ value: MerchantPersonRole; label: string }>;
  givenName: string;
  familyName: string;
  dateOfBirth: string;
  address: Address;
  emailAddress: string;
  phoneNumber: string;
  passport?: {
    country: Country;
    passportNumber: string;
    expiryDate: string;
  };
  driverLicence?: {
    country: Country;
    driverLicenceNumber: string;
    documentNumber?: string;
    versionNumber?: string;
    state?: string;
  };
  verificationDocs: {
    idType: 'Passport' | 'DriverLicence';
    docBackFileId?: string;
    docFrontFileId?: string;
    docBackFile?: File;
    docFrontFile?: File;
  };
};

type ToCreateMerchantPersonRequest = (userForm: PersonFormProps) => CreateMerchantPersonRequest;
type ToUpdateMerchantPersonRequest = (userForm: PersonFormProps) => UpdateMerchantPersonRequest;
type ToPersonFormProps = (person: MerchantPerson) => PersonFormProps;

export const toCreateMerchantPersonRequest: ToCreateMerchantPersonRequest = (
  personForm: PersonFormProps,
): CreateMerchantPersonRequest => {
  return {
    title: personForm.title,
    roles: personForm.roles.map((r) => r.value),
    givenName: personForm.givenName,
    familyName: personForm.familyName,
    dateOfBirth: personForm.dateOfBirth,
    address: ['AU', 'US'].includes(personForm.address.country)
      ? { ...personForm.address, city: undefined }
      : { ...personForm.address, state: undefined },
    emailAddress: personForm.emailAddress,
    phoneNumber: personForm.phoneNumber,
    passport: personForm.verificationDocs.idType === 'Passport' ? personForm.passport : undefined,
    driverLicence:
      personForm.verificationDocs.idType === 'DriverLicence'
        ? {
            country: personForm.driverLicence?.country as Country,
            driverLicenceNumber: personForm.driverLicence?.driverLicenceNumber as string,
            state: personForm.driverLicence?.country !== 'NZ' ? personForm.driverLicence?.state : undefined,
            ...(personForm.driverLicence?.country === 'NZ'
              ? { versionNumber: personForm.driverLicence?.versionNumber }
              : { documentNumber: personForm.driverLicence?.documentNumber }),
          }
        : undefined,
    verificationDocs: {
      idType: personForm.verificationDocs.idType ?? defaultIdType,
      docBackFileId:
        personForm.verificationDocs.idType === 'DriverLicence' ? personForm.verificationDocs.docBackFileId : undefined,
      docFrontFileId: personForm.verificationDocs.docFrontFileId ?? '',
    },
  };
};

export const toUpdateMerchantPersonRequest: ToUpdateMerchantPersonRequest = (
  personForm: PersonFormProps,
): UpdateMerchantPersonRequest => {
  return {
    title: personForm.title,
    roles: personForm.roles.map((r) => r.value),
    givenName: personForm.givenName,
    familyName: personForm.familyName,
    dateOfBirth: personForm.dateOfBirth,
    address: ['AU', 'US'].includes(personForm.address.country)
      ? { ...personForm.address, city: undefined }
      : { ...personForm.address, state: undefined },
    emailAddress: personForm.emailAddress,
    phoneNumber: personForm.phoneNumber,
    passport: personForm.verificationDocs.idType === 'Passport' ? personForm.passport : undefined,
    driverLicence:
      personForm.verificationDocs.idType === 'DriverLicence'
        ? {
            country: personForm.driverLicence?.country as Country,
            driverLicenceNumber: personForm.driverLicence?.driverLicenceNumber as string,
            state: personForm.driverLicence?.country !== 'NZ' ? personForm.driverLicence?.state : undefined,
            ...(personForm.driverLicence?.country === 'NZ'
              ? { versionNumber: personForm.driverLicence?.versionNumber }
              : { documentNumber: personForm.driverLicence?.documentNumber }),
          }
        : undefined,
    verificationDocs: {
      idType: personForm.verificationDocs.idType ?? defaultIdType,
      docBackFileId:
        personForm.verificationDocs.idType === 'DriverLicence' ? personForm.verificationDocs.docBackFileId : undefined,
      docFrontFileId: personForm.verificationDocs.docFrontFileId ?? '',
    },
  };
};

export const toPersonFormProps: ToPersonFormProps = (person: MerchantPerson): PersonFormProps => {
  return {
    ownerId: person.ownerId,
    title: person.profile.title,
    roles: merchantPersonRoles.filter((r) => person.profile.roles.includes(r.value as MerchantPersonRole)),
    givenName: person.profile.givenName,
    familyName: person.profile.familyName,
    dateOfBirth: person.profile.dateOfBirth,
    address: person.profile.address,
    emailAddress: person.profile.emailAddress,
    phoneNumber: person.profile.phoneNumber,
    passport: {
      country: person.profile.passport?.country ?? person.profile.driverLicence?.country ?? defaultCountryId,
      passportNumber: person.profile.passport?.passportNumber ?? '',
      expiryDate: person.profile.passport?.expiryDate ?? '',
    },
    driverLicence: {
      ...person.profile.driverLicence,
      country: person.profile.driverLicence?.country ?? person.profile.passport?.country ?? defaultCountryId,
      driverLicenceNumber: person.profile.driverLicence?.driverLicenceNumber ?? '',
    },
    verificationDocs: {
      idType: person.profile.verificationDocs?.idType ?? defaultIdType,
      docBackFileId: person.profile.verificationDocs?.docBackFileId,
      docFrontFileId: person.profile.verificationDocs?.docFrontFileId,
    },
  };
};
